import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { extendSession, logoutUser } from '../../redux/action/actions';

const SessionExpiryWarning = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const { user } = useSelector(state => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    if (user && user.expirationTime) {
      const checkExpiration = () => {
        const now = new Date();
        const expiration = new Date(user.expirationTime);
        const diff = expiration.getTime() - now.getTime();
        
        // Show warning if less than 1 hour left
        const oneHour = 60 * 60 * 1000;
        
        if (diff <= 0) {
          setShowWarning(false);
          setTimeLeft(null);
        } else if (diff <= oneHour) {
          const minutes = Math.floor(diff / (1000 * 60));
          setTimeLeft(minutes);
          setShowWarning(true);
        } else {
          setShowWarning(false);
          setTimeLeft(null);
        }
      };

      checkExpiration();
      const interval = setInterval(checkExpiration, 30000); // Check every 30 seconds

      return () => clearInterval(interval);
    } else {
      setShowWarning(false);
      setTimeLeft(null);
    }
  }, [user]);

  const handleExtendSession = () => {
    dispatch(extendSession());
    setShowWarning(false);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    setShowWarning(false);
  };

  if (!showWarning || !timeLeft) return null;

  return (
    <div className="fixed top-20 right-4 z-50 animate-slideIn">
      <div className="bg-black/95 border border-red-400/50 rounded-xl p-4 shadow-neon-pink backdrop-blur-md max-w-sm relative overflow-hidden">
        {/* Cyberpunk decorative elements */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-400 to-transparent animate-slide-right"></div>
        <div className="absolute -top-10 -right-10 w-20 h-20 bg-red-400/20 rounded-full blur-lg"></div>

        {/* Warning Header */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-black/60 border border-red-400/50 rounded-lg flex items-center justify-center shadow-neon-pink">
              <svg className="w-5 h-5 text-red-400 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 16h2v2h-2v-2zm0-6h2v4h-2v-4z"/>
              </svg>
            </div>
            <div>
              <h4 className="font-black font-mono text-red-300 text-sm">
                [SESSION_WARNING]
              </h4>
              <p className="text-xs text-red-400/80 font-mono">
                {'>'} Authentication expires soon
              </p>
            </div>
          </div>

          {/* Time remaining */}
          <div className="bg-black/60 border border-red-400/30 rounded-lg p-3 mb-4">
            <div className="text-center">
              <div className="text-xl font-black font-mono text-red-300 mb-1">
                {timeLeft}
              </div>
              <div className="text-xs text-red-400 font-mono">
                [MINUTES_REMAINING]
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-2">
            <button
              onClick={handleExtendSession}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black/80 border border-green-400/50 text-green-300 hover:text-green-100 hover:border-green-400 rounded-lg font-mono font-bold transition-all duration-300 shadow-neon-green hover:shadow-neon-green-intense transform hover:scale-105"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>[EXTEND_7_DAYS]</span>
            </button>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-black/80 border border-gray-400/50 text-gray-300 hover:text-gray-100 hover:border-gray-400 rounded-lg font-mono font-bold transition-all duration-300 transform hover:scale-105"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>[LOGOUT_NOW]</span>
            </button>
          </div>

          {/* Close button */}
          <button
            onClick={() => setShowWarning(false)}
            className="absolute -top-2 -right-2 w-6 h-6 bg-black/80 border border-red-400/50 rounded-full flex items-center justify-center text-red-400 hover:text-red-300 transition-colors duration-200"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slide-right {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }

        .animate-slide-right { 
          animation: slide-right 3s ease-in-out infinite; 
        }

        .shadow-neon-pink { 
          box-shadow: 0 0 20px rgba(255,0,128,0.5), 0 0 40px rgba(255,0,128,0.3), 0 0 60px rgba(255,0,128,0.1); 
        }
        
        .shadow-neon-green { 
          box-shadow: 0 0 20px rgba(0,255,128,0.5), 0 0 40px rgba(0,255,128,0.3), 0 0 60px rgba(0,255,128,0.1); 
        }
        
        .shadow-neon-green-intense { 
          box-shadow: 0 0 30px rgba(0,255,128,0.7), 0 0 60px rgba(0,255,128,0.5), 0 0 90px rgba(0,255,128,0.3); 
        }
      `}</style>
    </div>
  );
};

export default SessionExpiryWarning; 